import { Badge } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { formatTaskEntityType } from '../../../utils/textCase.js';
import { shortEventId } from '../../../utils/formatEventId.js';

function entityBadgeVariant(entityType) {
  switch (String(entityType ?? '').toLowerCase()) {
    case 'change_request':
      return 'purple';
    case 'claim':
      return 'warning';
    case 'truth':
      return 'lightBlue';
    default:
      return 'neutral';
  }
}

function severityBadgeVariant(severityLabel) {
  switch (String(severityLabel ?? '').toLowerCase()) {
    case 'critical':
      return 'danger';
    case 'high':
      return 'alert';
    case 'medium':
      return 'warning';
    case 'low':
      return 'success';
    default:
      return 'neutral';
  }
}

/**
 * Compact task preview for inbox rows and card view — message snippet, linked
 * entity chip, severity, and optional assignee (card view has no OwnerChip row).
 */
function TaskPreview({ event, showAssignee = false }) {
  const { t } = useTranslation('work-queues-app');
  const detail = event.detail ?? {};
  const message = event.situation ?? event.description ?? null;
  const entityType = formatTaskEntityType(detail.entityType, t);
  const entityRef = detail.entityUuid ? shortEventId(detail.entityUuid) : null;
  const severityLabel = detail.severityLabel ?? null;

  if (!message && !entityType && !severityLabel) return null;

  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      {message ? (
        <p className="line-clamp-2 text-[11px] leading-snug text-content-secondary">{message}</p>
      ) : null}

      <div className="flex flex-wrap items-center gap-1">
        {entityType && (
          <Badge variant={entityBadgeVariant(detail.entityType)} size="small">
            {t('task.linkedEntityShort', { entity: entityType })}
          </Badge>
        )}
        {entityRef && (
          <Badge variant="neutral" size="small">
            {entityRef}
          </Badge>
        )}
        {severityLabel && (
          <Badge variant={severityBadgeVariant(severityLabel)} size="small">
            {severityLabel}
          </Badge>
        )}
        {showAssignee && event.owner?.name && (
          <Badge variant="informative" size="small">
            {event.owner.name}
          </Badge>
        )}
      </div>
    </div>
  );
}

export default TaskPreview;
